// A11y for the existing Releasit dialog. No form fields, payload or submit handlers are changed.
(() => {
  const closeLabel = (document.currentScript?.dataset.closeDialog || '').trim();
  if (!closeLabel || !document.body) return;
  const selector = 'dialog#rsi_form_wrapper';
  const states = new WeakMap();
  let mountedDialog = null;
  let pendingOpener = null;
  let lastOutsideFocus = null;
  let keySequence = null;
  const hasName = (element) => Boolean(
    element.getAttribute('aria-label')?.trim() ||
    element.getAttribute('aria-labelledby')?.trim()
  );
  const activationKey = (event) => event.key === 'Enter' ? 'Enter' :
    (event.key === ' ' || event.key === 'Spacebar' ? 'Space' : null);
  const outsideDialog = (element) => element instanceof HTMLElement &&
    element !== document.body && element !== document.documentElement &&
    !element.closest(selector);

  function usableOpener(element) {
    return outsideDialog(element) && element.isConnected &&
      !element.matches(':disabled') && !element.closest('[inert]') &&
      element.getClientRects().length > 0 &&
      getComputedStyle(element).visibility === 'visible';
  }

  function rememberOpener(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[id="rsi_buy_now_button"]');
    if (usableOpener(button) && !document.querySelector(selector)?.open) {
      pendingOpener = button;
    }
  }

  function enhanceNames(dialog) {
    const close = dialog.querySelector('button.rsi-form-close-button');
    if (close) {
      if (close.type !== 'button') close.type = 'button';
      if (!hasName(close) && !close.textContent.trim() &&
          !close.getAttribute('title')?.trim()) {
        close.setAttribute('aria-label', closeLabel);
      }
    }
    if (!hasName(dialog)) {
      const title = dialog.querySelector('.rsi-form-header')
        ?.textContent.replace(/\s+/g, ' ').trim();
      if (title) dialog.setAttribute('aria-label', title);
    }
  }

  function restoreFocus(dialog, state) {
    if (state.restorePending) return;
    const opener = state.opener;
    state.restorePending = true;
    requestAnimationFrame(() => {
      state.restorePending = false;
      if (document.querySelector(selector)?.open || !usableOpener(opener)) return;
      const active = document.activeElement;
      if (active && active !== document.body && active !== document.documentElement &&
          !dialog.contains(active)) return;
      opener.focus({ preventScroll: true });
    });
  }

  function syncDialog(dialog) {
    const state = states.get(dialog);
    if (!state) return;
    const open = dialog.isConnected && dialog.open;
    if (open && !state.open) {
      const active = document.activeElement;
      state.opener = usableOpener(pendingOpener) ? pendingOpener :
        (usableOpener(active) ? active :
          (usableOpener(lastOutsideFocus) ? lastOutsideFocus : null));
      pendingOpener = null;
    } else if (!open && state.open) {
      restoreFocus(dialog, state);
    }
    state.open = open;
  }

  function mount() {
    const dialog = document.querySelector(selector);
    if (mountedDialog && mountedDialog !== dialog) {
      syncDialog(mountedDialog);
      states.get(mountedDialog)?.observer.disconnect();
    }
    mountedDialog = dialog;
    if (!dialog) return;
    enhanceNames(dialog);
    if (!states.has(dialog)) {
      const state = { open: false, opener: null, restorePending: false, observer: null };
      state.observer = new MutationObserver(() => syncDialog(dialog));
      states.set(dialog, state);
      dialog.addEventListener('close', () => syncDialog(dialog));
    }
    states.get(dialog).observer.observe(dialog, {
      attributes: true, attributeFilter: ['open']
    });
    syncDialog(dialog);
  }

  function handleCloseKey(event) {
    const key = activationKey(event);
    if (!key || event.isComposing || event.altKey || event.ctrlKey || event.metaKey) return;
    const code = event.code || key;
    const continuation = keySequence?.key === key && keySequence.code === code;
    if (!continuation) {
      if (event.type !== 'keydown' || !(event.target instanceof Element)) return;
      const close = event.target.closest('button.rsi-form-close-button');
      const dialog = close?.closest(selector);
      if (!dialog?.open || close.disabled) return;
      keySequence = { key, code, close, dialog, activated: false };
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const sequence = keySequence;
    if (event.type === 'keydown' && key === 'Enter' && !event.repeat &&
        !sequence.activated) {
      sequence.activated = true;
      sequence.close.type = 'button';
      HTMLElement.prototype.click.call(sequence.close);
    } else if (event.type === 'keyup') {
      keySequence = null;
      if (key === 'Space' && sequence.close.isConnected && !sequence.close.disabled &&
          sequence.dialog.open && document.activeElement === sequence.close) {
        sequence.close.type = 'button';
        HTMLElement.prototype.click.call(sequence.close);
      }
    }
  }

  window.addEventListener('pointerdown', rememberOpener, true);
  window.addEventListener('click', rememberOpener, true);
  window.addEventListener('keydown', (event) => {
    if (activationKey(event)) rememberOpener(event);
    handleCloseKey(event);
  }, true);
  window.addEventListener('keypress', handleCloseKey, true);
  window.addEventListener('keyup', handleCloseKey, true);
  window.addEventListener('blur', () => { keySequence = null; });
  document.addEventListener('focusin', (event) => {
    if (outsideDialog(event.target)) lastOutsideFocus = event.target;
  }, true);

  function touchesDialog(record) {
    const target = record.target.nodeType === 1
      ? record.target : record.target.parentElement;
    if (target?.closest(selector)) return true;
    return [...record.addedNodes, ...record.removedNodes].some((node) =>
      node.nodeType === 1 && (node.matches(selector) ||
        node.querySelector(selector) || (mountedDialog && node.contains(mountedDialog))));
  }
  mount();
  new MutationObserver((records) => {
    if (records.some(touchesDialog)) mount();
  }).observe(document.body, { childList: true, subtree: true });
})();
